import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useLocation } from "react-router-dom";

import { capitalize } from "lodash";
import { useQueryClient } from "react-query";

import { fetchClientHorizon } from "./useClientHorizon";
import { decrypt, encrypt, encodeBase64 } from "../utils/crypto";
import api from "../services/api";
import { notifyError } from "../utils/notifications";
import { storageService } from "../services/storageService";
import { CardFinancialData, ClientContextData } from "./ClientProvider.types";
import { getClientDisplayName } from "../utils/socialName";

// To do: tipar esse contexto e alterar nos arquivos tsx que vão reclamar da mudança de tipagem

export type ClientTitular = {
  id: number;
  name: string;
  cpf?: string;
  socialName?: string;
  allowsSocialName?: boolean;
  dataCadastro?: string | null;
} & Record<any, any>;

export const normalizeBoolean = (value: unknown): boolean =>
  value === true || value === "S" || value === "s" || value === "SIM";

const normalizeIsProdutoBandeirado = (value: unknown) =>
  value === true || value === "S";

const ClientContext = createContext<ClientContextData>({} as ClientContextData);

interface ClientProviderInterface {
  children: ReactNode;
}

export function ClientProvider({ children }: ClientProviderInterface) {
  const queryClient = useQueryClient();

  const [client, setClient] = useState(() => storageService.getClient());
  const [isLoadingClient, setIsLoadingClient] = useState(false);
  const [cardFinancial, setCardFinancial] = useState<CardFinancialData | null>(null);
  const [isLoadingCardFinancial, setIsLoadingCardFinancial] = useState<boolean>(false);
  const [isErrorCardFinancial, setIsErrorCardFinancial] = useState<boolean>(false);

  const refreshCardFinancial = useCallback(async () => {
    const clientId = client?.client?.id;
    if (!clientId) return;

    setIsLoadingCardFinancial(true);
    setIsErrorCardFinancial(false);

    try {
      const response = await api.get("client_information", { client_id: clientId });
      const data = response?.data || {};

      setCardFinancial({
        limitePrincipal: Number(data.limitePrincipal) || 0,
        saldoDisponivel: Number(data.saldoDisponivel) || 0,
        limiteParcelado: Number(data.limiteParcelado) || 0,
        saldoDisponivelParcelado: Number(data.saldoDisponivelParcelado) || 0,
        limiteSaque: Number(data.limiteSaque) || 0,
        saldoDisponivelSaque: Number(data.saldoDisponivelSaque) || 0,
        devedorRotativo: Number(data.devedorRotativo) || 0,
        devedorParcelado: Number(data.devedorParcelado) || 0,
        saldoDisponivelRotativo: Number(data.saldoDisponivelRotativo) || 0
      });
    } catch (err) {
      console.error("Erro ao carregar cardFinancial:", err);setIsErrorCardFinancial(true);

      setCardFinancial({
        limitePrincipal: 0,
        saldoDisponivel: 0,
        limiteParcelado: 0,
        saldoDisponivelParcelado: 0,
        limiteSaque: 0,
        saldoDisponivelSaque: 0,
        devedorRotativo: 0,
        devedorParcelado: 0,
        saldoDisponivelRotativo: 0,
      });
    } finally {
      setIsLoadingCardFinancial(false);
    }
}, [client?.client?.id]);

  useEffect(() => {
    if (client?.client?.id) {
      refreshCardFinancial();
    } else {
      setCardFinancial(null);
    }
  }, [client?.client?.id, refreshCardFinancial]);

  useEffect(() => {
    storageService.clearLegacySocialNameFlag();
  }, []);

  useEffect(() => {
    const displayName = getClientDisplayName(client?.client);
    if (displayName) {
      document.title = `${displayName} - RPE - Atendimento`;
    } else {
      document.title = "RPE - Atendimento";
    }

    return () => { document.title = "RPE - Atendimento"; };
  }, [client]);

  const updateClient = useCallback((newClient) => {
    storageService.setClient(newClient);
    setClient(newClient);
  }, []);

  const clearClient = useCallback(() => {
    const currentId = client?.client?.id;

    storageService.clearAttendance();
    setClient(undefined);
    setCardFinancial(null);

    if (currentId) {
      queryClient.removeQueries(["clientMaster", currentId]);
      queryClient.removeQueries(["clientId", currentId]);
    }
  }, [client?.client?.id, queryClient]);

  const refreshClient = useCallback(async () => {
    const clientId = client?.client?.id;
    if (!clientId) return;

    const {
      transicaoBandeira,
      produtoTransicaoBandeira,
      produto: { id: codigoProduto, isProdutoBandeirado, descricao, isProdutoDigital },
    } = await fetchClientHorizon(queryClient, clientId, { force: true });

    const updatedClient = {
      ...client,
      client: {
        ...client.client,
        transicaoBandeira,
        isProdutoBandeirado: normalizeIsProdutoBandeirado(isProdutoBandeirado),
        produtoTransicaoBandeira,
      },
      product: {
        ...client.product,
        codigoProduto,
        desc_prod: descricao,
        isProdutoDigital,
      },
    };

    updateClient(updatedClient);
  }, [client, queryClient, updateClient]);

  const refreshClientV2 = useCallback(
    async (
      usePreviousClientData = true,
      productIdOverride: number | undefined = undefined,
      forceHorizonRefresh = true
    ) => {

      if (!client?.client?.id) {
        return;
      }

      try {
     setIsLoadingClient(true);
        const targetProductId = productIdOverride ?? client?.product?.codigoProduto;

        const clientFound = await queryClient.fetchQuery(
          ["clientMaster", client.client.id, targetProductId],
          async () => {
            const res = await api.get("client_id", {
              client_id: client.client.id,
              product_id: targetProductId,
              shouldLoadTicket: true,
              ticketId: client.client.ticket?.id,
              force_refresh: forceHorizonRefresh, 
            });
            return res.data;
          },
          {
            staleTime: forceHorizonRefresh ? 0 : Infinity, 
          }
        );

        const productUpdate = {
          ...client?.product,
          codigoProduto: clientFound?.produto?.id ?? targetProductId,
          desc_prod: clientFound?.produto?.descricao,
          isProdutoDigital: clientFound?.produto?.isProdutoDigital,
        };
        
        const clientHorizonFields = {
          transicaoBandeira: clientFound?.transicaoBandeira,
          isProdutoBandeirado: normalizeIsProdutoBandeirado(
            clientFound?.produto?.isProdutoBandeirado ?? clientFound?.isProdutoBandeirado),
          produtoTransicaoBandeira: clientFound?.produtoTransicaoBandeira,
          };

        if (clientFound.endereco_alternativo) {
          clientFound.endereco_alternativo.bairro = capitalize(
            clientFound.endereco_alternativo.bairro
              ? clientFound.endereco_alternativo.bairro
              : ""
          );
          clientFound.endereco_alternativo.localidade = capitalize(
            clientFound.endereco_alternativo.localidade
              ? clientFound.endereco_alternativo.localidade
              : ""
          );
          clientFound.endereco_alternativo.cidade = capitalize(
            clientFound.endereco_alternativo.cidade
              ? clientFound.endereco_alternativo.cidade
              : ""
          );
          clientFound.endereco_alternativo.logradouro = capitalize(
            clientFound.endereco_alternativo.logradouro
              ? clientFound.endereco_alternativo.logradouro
              : ""
          );
          clientFound.endereco_alternativo.complemento = capitalize(
            clientFound.endereco_alternativo.complemento
              ? clientFound.endereco_alternativo.complemento
              : ""
          );
        }

        if (clientFound.endereco_comercial) {
          clientFound.endereco_comercial.empresa = capitalize(
            clientFound.endereco_comercial.empresa
              ? clientFound.endereco_comercial.empresa
              : ""
          );
          clientFound.endereco_comercial.departamento = capitalize(
            clientFound.endereco_comercial.departamento
              ? clientFound.endereco_comercial.departamento
              : ""
          );
          clientFound.endereco_comercial.bairro = capitalize(
            clientFound.endereco_comercial.bairro
              ? clientFound.endereco_comercial.bairro
              : ""
          );
          clientFound.endereco_comercial.localidade = capitalize(
            clientFound.endereco_comercial.localidade
              ? clientFound.endereco_comercial.localidade
              : ""
          );
          clientFound.endereco_comercial.cidade = capitalize(
            clientFound.endereco_comercial.cidade
              ? clientFound.endereco_comercial.cidade
              : ""
          );
          clientFound.endereco_comercial.logradouro = capitalize(
            clientFound.endereco_comercial.logradouro
              ? clientFound.endereco_comercial.logradouro
              : ""
          );
          clientFound.endereco_comercial.complemento = capitalize(
            clientFound.endereco_comercial.complemento
              ? clientFound.endereco_comercial.complemento
              : ""
          );
        }

        if (clientFound.endereco_residencial) {
          clientFound.endereco_residencial.bairro = capitalize(
            clientFound.endereco_residencial.bairro
              ? clientFound.endereco_residencial.bairro
              : ""
          );
          clientFound.endereco_residencial.localidade = capitalize(
            clientFound.endereco_residencial.localidade
              ? clientFound.endereco_residencial.localidade
              : ""
          );
          clientFound.endereco_residencial.cidade = capitalize(
            clientFound.endereco_residencial.cidade
              ? clientFound.endereco_residencial.cidade
              : ""
          );
          clientFound.endereco_residencial.logradouro = capitalize(
            clientFound.endereco_residencial.logradouro
              ? clientFound.endereco_residencial.logradouro
              : ""
          );
          clientFound.endereco_residencial.complemento = capitalize(
            clientFound.endereco_residencial.complemento
              ? clientFound.endereco_residencial.complemento
              : ""
          );
        }

        const decryptedBirthday = clientFound.birthday
          ? decrypt(clientFound.birthday)
          : undefined;

        const insideClient = usePreviousClientData
          ? {
            ...client.client,
            ...clientFound,
            ...clientHorizonFields,
            birthday: decryptedBirthday,
          }
          : {
            ...clientFound,
            ticket: clientFound.ticket || client.client.ticket,
            ...clientHorizonFields,
            birthday: decryptedBirthday,
          };

        updateClient({
          ...client,
          client: insideClient,
          product: productUpdate,
        });

      } catch (err) {
        const error = err as ErrorMessage;
        console.error("Erro ao atualizar dados do portador: ", error.message);
        notifyError("Houve um erro ao atualizar os dados do portador");
      } finally {
        setIsLoadingClient(false);
      }
    },
    [client, queryClient, updateClient]
  );

  const contextValue = useMemo(
    () => ({
      client,
      cardFinancial,
      updateClient,
      clearClient,
      refreshClient,
      refreshClientV2,
      refreshCardFinancial,
      isLoadingClient,
    }),
    [
      client,
      cardFinancial,
      updateClient,
      clearClient,
      refreshClient,
      refreshClientV2,
      refreshCardFinancial,
      isLoadingClient,
    ]
  );

  useEffect(() => {
    if (client?.client?.id) {
      refreshClientV2(false, undefined, false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.client?.id]);

  return (
    <ClientContext.Provider value={contextValue}>{children}</ClientContext.Provider>
  );
}

export function useClient() {
  const context = useContext(ClientContext);

  if (!context) {
    throw new Error("useClient must be used within an ClientProvider");
  }

  return context;
}

const App = ({ children }: { children: ReactNode }) => {
  const location = useLocation();

  return location.pathname !== "/" ? (
    <ClientProvider>{children}</ClientProvider>
  ) : (
    <>{children}</>
  );
};

export default App;
